import { Toaster } from "@/components/ui/sonner";
import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import DashboardLayout from "./components/DashboardLayout";
import Home from "./pages/Home";
const Screening = lazy(() => import("./pages/Screening"));
const Tradability = lazy(() => import("./pages/Tradability"));
import More from "./pages/More";
import Positions from "./pages/Positions";
import Records from "./pages/Records";
const Reports = lazy(() => import("./pages/Reports"));
import Config from "./pages/Config";
const LlmManage = lazy(() => import("./pages/LlmManage"));
import Broker from "./pages/Broker";
const AgentManage = lazy(() => import("./pages/AgentManage"));
const ExtremeSimMonitor = lazy(() => import("./pages/ExtremeSimMonitor"));
const ExtremePush = lazy(() => import("./pages/ExtremePush"));
const SwingSignal = lazy(() => import("./pages/SwingSignal"));
const SwingTrade = lazy(() => import("./pages/SwingTrade"));
const T0History = lazy(() => import("./pages/T0History"));
const ProfitGuardian = lazy(() => import("./pages/ProfitGuardian"));
// [Claude 2026-05-19] StockDetail 暂禁用: lightweight-charts v5 API 不兼容, 待重写
// const StockDetail = lazy(() => import("./pages/StockDetail"));
const StockAnalysis = lazy(() => import("./pages/StockAnalysis"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const RecommendationTracker = lazy(() => import("./pages/RecommendationTracker"));
const MyTracking = lazy(() => import("./pages/MyTracking"));
const LongTermOpportunities = lazy(() => import("./pages/LongTermOpportunities"));
const Integration = lazy(() => import("./pages/Integration"));
import Notifications from "./pages/Notifications";
const EmtExport = lazy(() => import("./pages/EmtExport"));
const StrategyRuns = lazy(() => import("./pages/StrategyRuns"));
const EngineVersions = lazy(() => import("./pages/EngineVersions"));
const TokenStats = lazy(() => import("./pages/TokenStats"));
import Profile from "./pages/Profile";
const ParamAnalysis = lazy(() => import("./pages/ParamAnalysis"));
const NewsMining = lazy(() => import("./pages/NewsMining"));
const AiSignals = lazy(() => import("./pages/AiSignals"));
const HaManagement = lazy(() => import("./pages/HaManagement"));
const L2Stability = lazy(() => import("./pages/L2Stability"));
const StockProfiles = lazy(() => import("./pages/StockProfiles"));
// [Claude 2026-05-22 Phase 2.5] Admin 管理后台
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminInvites = lazy(() => import("./pages/AdminInvites"));
const AdminAudit = lazy(() => import("./pages/AdminAudit"));
// [Claude 2026-05-22 Phase 2.5] 用户端钱包 + 个人中心
const MyWallet = lazy(() => import("./pages/MyWallet"));
const MeIndex = lazy(() => import("./pages/MeIndex"));
const MeSubscription = lazy(() => import("./pages/MeSubscription"));
const MeSecurity = lazy(() => import("./pages/MeSecurity"));
const MeUsers = lazy(() => import("./pages/MeUsers"));  // [Claude 20260522]

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      {/* [Claude 2026-05-22 Phase 2.5] /register 重启 — 邀请码必填的真正注册页 */}
      <Route path="/register" component={Register} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <DashboardLayout>
        <Suspense fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",color:"#64748b",fontSize:"14px"}}>加载中...</div>}>
          <Switch>
            <Route path="/" component={StockAnalysis} />
            <Route path="/screening" component={Screening} />
            <Route path="/tradability" component={Tradability} />
            <Route path="/more" component={More} />
            <Route path="/positions" component={Positions} />
            <Route path="/records" component={Records} />
            <Route path="/reports" component={Reports} />
            <Route path="/config" component={Config} />
            <Route path="/llm" component={LlmManage} />
            <Route path="/broker" component={Broker} />
            <Route path="/agent" component={AgentManage} />
            <Route path="/extreme-sim" component={ExtremeSimMonitor} />
            <Route path="/extreme-push" component={ExtremePush} />
            <Route path="/swing-signal" component={SwingSignal} />
            {/* [15b 20260815] 波段已并入 /agent?tab=swing; 本路由保留重定向, Steven 书签不失效 */}
            <Route path="/swing-trade">{() => { window.location.replace("/agent?tab=swing"); return null; }}</Route>
            <Route path="/t0-history" component={T0History} />
            <Route path="/guardian" component={ProfitGuardian} />
            <Route path="/opportunities" component={Opportunities} />
            <Route path="/recommendations" component={RecommendationTracker} />
            <Route path="/my-tracking" component={MyTracking} />
            <Route path="/long-term" component={LongTermOpportunities} />
            <Route path="/ai-signals" component={AiSignals} />
            <Route path="/integration" component={Integration} />
            <Route path="/notifications" component={Notifications} />
            <Route path="/emt-export" component={EmtExport} />
            <Route path="/strategy-runs" component={StrategyRuns} />
            <Route path="/engine-versions" component={EngineVersions} />
            <Route path="/ha" component={HaManagement} />
            <Route path="/l2-stability" component={L2Stability} />
            <Route path="/stock-profiles" component={StockProfiles} />
            <Route path="/token-stats" component={TokenStats} />
            <Route path="/profile" component={Profile} />
            <Route path="/param-analysis" component={ParamAnalysis} />
            <Route path="/news-mining" component={NewsMining} />
            {/* <Route path="/stock/:code" component={StockDetail} /> [Claude 2026-05-19] 暂禁用 */}
            {/* [Claude 2026-05-22 Phase 2.5] Admin 后台 (仅 super_admin 可访问, 前端不校验, 后端 superAdminProcedure 拦截) */}
            <Route path="/admin" component={AdminDashboard} />
            <Route path="/admin/users" component={AdminUsers} />
            <Route path="/admin/invites" component={AdminInvites} />
            <Route path="/admin/audit" component={AdminAudit} />
            {/* [Claude 2026-05-22 Phase 2.5] 用户端: 个人中心 / 钱包 / 订阅 / 安全 (protectedProcedure) */}
            <Route path="/me/wallet" component={MyWallet} />
            <Route path="/me/subscription" component={MeSubscription} />
            <Route path="/me/security" component={MeSecurity} />
            <Route path="/me/users" component={MeUsers} />{/* [Claude 20260522] admin+ */}
            <Route path="/me" component={MeIndex} />
            <Route path="/404" component={NotFound} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </DashboardLayout>
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
